import chroma from 'chroma-js';
import { useMemo } from 'react';
import i18n from '../../../i18n';
import { palette } from '../../../theme/colors';
import { translate } from '../../../utils/translate';
import { dateFormats, getDateFormatCustom } from '../../../utils/date';
import type { Chat } from '../../../containers/ChatsList/chatListSlice/types';
import {
  SAlarmName,
  SBoldValue,
  SContainer,
  SContent,
  SLabel,
  SOrganizationName,
  SRow,
  SSectionContainer,
  SSectionText,
  SSectionTitle,
  SSentTime,
  SSentTimeContainer,
  SSeparator,
} from './TemplateBasedAlarmMessage.styles';

type Props = {
  message: Chat;
  senderName: string;
  messageTo: string | null;
};

export const TemplateBasedAlarmMessage = ({ message, senderName, messageTo }: Props) => {
  const alarmColor = useMemo(
    () =>
      message.emergencyTypeColor && chroma.valid(message.emergencyTypeColor)
        ? message.emergencyTypeColor
        : palette.redAlert,
    [message.emergencyTypeColor]
  );

  const instructions =
    message.emergencyTypePrewrittenText ||
    message.emergencyTypePrewrittenTexts?.find(t => t.Language === i18n.language)?.Text ||
    message.emergencyTypePrewrittenTexts?.find(t => t.Language === 'en')?.Text;

  const hasInstructions = !!instructions;
  const organizationName = message.Organization?.name;
  const hasOrganizationName = !!organizationName;
  const emergencyTypeName = message.emergencyTypeName;
  const hasEmergencyTypeName = !!emergencyTypeName;
  const sentTime = getDateFormatCustom(message.sent, dateFormats.simpleTime);
  const sentTimeElement = sentTime && <SSentTime>{sentTime}</SSentTime>;

  return (
    <SContainer $borderColor={alarmColor} role="templateBasedAlarmMessage">
      <SContent>
        <div>
          <SRow>
            <SLabel>{translate('sent_from')}</SLabel>
            <SBoldValue>{senderName}</SBoldValue>
          </SRow>

          {messageTo && (
            <SRow>
              <SLabel>{translate('messages_to')}</SLabel>
              <SBoldValue>{messageTo}</SBoldValue>
            </SRow>
          )}
        </div>

        {(hasEmergencyTypeName || hasOrganizationName) && (
          <div>
            {hasEmergencyTypeName && (
              <SAlarmName $color={alarmColor}>{emergencyTypeName.toUpperCase()}</SAlarmName>
            )}
            {hasOrganizationName && (
              <SSentTimeContainer>
                <SOrganizationName>{organizationName}</SOrganizationName>
                {!hasInstructions && sentTimeElement}
              </SSentTimeContainer>
            )}
          </div>
        )}

        {!hasOrganizationName && !hasInstructions && sentTimeElement}
      </SContent>

      {hasInstructions && (
        <>
          <SSeparator />
          <SSectionContainer>
            <SSectionTitle $color={alarmColor}>{translate('alarm_instructions')}</SSectionTitle>
            <SSentTimeContainer>
              <SSectionText>{instructions}</SSectionText>
              {sentTimeElement}
            </SSentTimeContainer>
          </SSectionContainer>
        </>
      )}
    </SContainer>
  );
};
